import { Controller } from '@hotwired/stimulus'
import Tribute from "tributejs";
import Trix from 'trix';

// Keep in sync with MentionsController::MAX_QUERY_LENGTH / MAX_QUERY_TERMS.
// A mention query is a short name prefix; once the text looks like prose
// (typing continued past an unresolved "@"), stop searching entirely.
const QUERY_DEBOUNCE_MS = 300;
const MAX_QUERY_LENGTH = 30;
const MAX_QUERY_TERMS = 3;

export default class extends Controller {
  static targets = ['input'];


 connect() {
    this.editor = this.inputTarget.editor
    this.boundReplaced = this.replaced.bind(this)
    this.initializeTribute()
  }

  disconnect() {
    clearTimeout(this.fetchTimer)
    this.abortController?.abort()
    if(this.hasInputTarget) {
      this.tribute.detach(this.inputTarget)
      // Without this, reconnects (Turbo morphs, Stimulus HMR) stack listeners and
      // every mention gets inserted once per stacked listener.
      this.inputTarget.removeEventListener("tribute-replaced", this.boundReplaced)
    }
  }

  initializeTribute() {
    // Tribute's caret-tracking math misplaces the menu inside our inner-scroll layouts
    // (it can land off-screen or cover the editor), so we opt out of its positioning
    // (positionMenu: false) and pin the menu to the editor's wrapper via CSS instead
    // (.mentions-menu-anchor in hyper-overrides.scss).
    const menuAnchor = this.inputTarget.parentElement
    menuAnchor.classList.add('position-relative', 'mentions-menu-anchor')

    this.tribute = new Tribute({
      values: (text, callback) => this.fetchUsers(text, callback),
      trigger: '@',
      menuContainer: menuAnchor,
      positionMenu: false,
      lookup: function (person, mentionText) {
        return person.name + person.email;
      },
      allowSpaces: true,
      menuItemTemplate: (item) => { return `<div class="d-flex justify-content-between"><div>${item.original.name}</div><div class="text-truncate">${item.original.email}</div></div>` }
    })
    this.tribute.attach(this.inputTarget)
    this.inputTarget.addEventListener("tribute-replaced", this.boundReplaced)
    this.tribute.range.pasteHtml = this._pasteHtml.bind(this)
  }

  fetchUsers(text, callback) {
    clearTimeout(this.fetchTimer)

    if (text.length > MAX_QUERY_LENGTH || text.split(/\s+/).length > MAX_QUERY_TERMS) {
      this.abortController?.abort()
      callback([])
      return
    }

    this.fetchTimer = setTimeout(() => {
      this.abortController?.abort()
      this.abortController = new AbortController()

      fetch(`/mentions.json?query=${encodeURIComponent(text)}`, { signal: this.abortController.signal })
        .then(response => response.json())
        .then(users => callback(users))
        .catch(error => { if (error.name !== 'AbortError') callback([]) })
    }, QUERY_DEBOUNCE_MS)
  }

  replaced(event) {
    event.stopPropagation();
    let mention = event.detail.item.original
    let attachment = new Trix.Attachment({
      sgid: mention.sgid,
      content: mention.content
    })
   event.target.editor.insertAttachment(attachment)
   // Trailing space so the next word doesn't butt up against the chip.
   event.target.editor.insertString(" ")
  }

   _pasteHtml(html, startPos, endPos) {
     let range = this.inputTarget.editor.getSelectedRange()
     let position = range[0]
     let length = endPos - startPos

     this.inputTarget.editor.setSelectedRange([position - length, position])
     this.inputTarget.editor.deleteInDirection("backward")
   }
}
